import * as React from "react";
import { Fragment } from "react";
import {
  DatagridConfigurable,
  DateField,
  ExportButton,
  FilterButton,
  SelectColumnsButton,
  TextField,
  TextInput,
  TopToolbar,
  useRecordContext,
  ShowButton,
  WrapperField,
  SelectInput,
  ReferenceField,
  CreateButton,
  FunctionField,
  downloadCSV,
  BulkUpdateButton,
  BulkDeleteButton,
  BulkExportButton,
} from "react-admin";
import { VisibilityOff } from "@mui/icons-material";

import { useCanAccess, IfCanAccess } from "@react-admin/ra-rbac";

import { useMediaQuery, Divider, Theme, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { statusText } from "../../components/Label";
import ComponentList from "../../components/PageComponents/ComponentList";
import { AccountFilter } from "../../components/PageComponents/ComponentList";
import {
  BulkActionButtons,
  ListButtons,
} from "../../components/PageComponents/ComponentList";
import MobileGrid from "./MobileGrid";
import { Helmet } from "react-helmet-async";
import { mapStatus, StatusLabel } from "../../components/Label/status";
import { FilterForm } from "../../components/PageComponents/ComponentList";
import { datagridSx } from "../../components/PageComponents/datagridSx";
import { formatCrypto, formatEuro, networkLabel } from "../../utils/paymentFormat";

const ListActions = ({ filters }: { filters: any[] }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 2,
      width: "100%",
      "& > .MuiToolbar-root": { flexShrink: 0 },
    }}
  >
    <FilterForm filters={filters} />
    <TopToolbar>
      <SelectColumnsButton />
      <IfCanAccess action="export" resource="cryptoOrders">
        <ExportButton exporter={customExporter} />
      </IfCanAccess>
      <IfCanAccess action="create" resource="cryptoOrders">
        <CreateButton />
      </IfCanAccess>
    </TopToolbar>
  </Box>
);

const OrderList = () => (
  <>
    <Helmet>
      <title>Coinhouse Solutions - Transactions</title>
    </Helmet>
    <ComponentList
      title="Transactions"
      subtitle="Every crypto payment received, with its amounts and status."
      /* filters={orderFilters} */
      actions={<ListActions filters={orderFilters} />}
      sort={{ field: "ulid", order: "DESC" }}
    >
      <TabbedDatagrid />
    </ComponentList>
  </>
);
const currencies = ["ETH", "USDC"];
const orderFilters = [
  /*   <DateInput source="dateCreated" />,
   */
  <AccountFilter />,
  <TextInput source="internalRef" label="Reference" helperText={false} />,
  <SelectInput
    source="currency"
    label="Currency"
    helperText={false}
    choices={Object.keys(currencies).map((key) => ({
      id: currencies[key],
      name: currencies[key].toUpperCase(),
    }))}
  />,
  <SelectInput
    source="statusOrder"
    label="Status"
    helperText={false}
    choices={Object.keys(mapStatus).map((key) => ({
      id: mapStatus[key].original,
      name: statusText(mapStatus[key].text),
    }))}
  />,
];

const customExporter = (data, fetchRelatedRecords, dataProvider, resource) => {
  const exportData = data.map((record) => ({
    dateCreated: record.dateCreated,
    euroAmount: record.euroAmount,
    cryptoAmount: record.amount,
    currency: record.currency,
    status: record.statusOrder,
  }));

  // Convert to CSV and download
  const csv = convertToCSV(exportData);
  downloadCSV(csv, "crypto-orders");
};

// Helper function to convert data to CSV
const convertToCSV = (data) => {
  if (!data || data.length === 0) return "";

  const headers = Object.keys(data[0]).join(",");
  const rows = data.map((item) =>
    Object.values(item)
      .map((value) =>
        typeof value === "string" ? `"${value.replace(/"/g, '""')}"` : value
      )
      .join(",")
  );

  return [headers, ...rows].join("\n");
};

const TabbedDatagrid = () => {
  const isXSmall = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down("sm")
  );
  const currency = useRecordContext()?.currency;
  return (
    <Fragment>
      <Divider />
      {isXSmall ? (
        <MobileGrid />
      ) : (
        <>
          <DatagridConfigurable
            bulkActionButtons={<BulkActionButtons ressource="cryptoOrders" />}
            rowClick="show"
            omit={["publicAddressDest", "tx_hash"]}
            sx={datagridSx}
          >
            <DateField
              source="dateCreated"
              showTime
              sortBy="ulid"
              locales="fr-FR"
              label="Created"
            />
            <TextField sortable={false}
              source="internalRef"
              label="Reference"
              />
            <WrapperField sortable={false} label="Status">
              <StatusLabel />
            </WrapperField>
            <FunctionField sortable={false} label="Euro amount" textAlign="right" render={(record) => (record.euroAmount !== undefined ? formatEuro(record.euroAmount) : "—")} />
            <FunctionField sortable={false} label="Payout amount" textAlign="right" render={(record) => (record.quoteAmount !== undefined ? formatEuro(record.quoteAmount) : "—")} />
            <FunctionField sortable={false} label="Crypto amount" textAlign="right" render={(record) => `${formatCrypto(record.amount)}\u00A0${String(record.currency || "").toUpperCase()}`} />
            <FunctionField sortable={false} label="Network" render={(record) => networkLabel(record.network) || "—"} />

            <ReferenceField
              source="accountId"
              reference="accounts"
              label="Account"
              sortable={false}
            >
              <TextField sortable={false} source="name" />
            </ReferenceField>
            <ListButtons ressource="cryptoOrders" />
          </DatagridConfigurable>
        </>
      )}
    </Fragment>
  );
};

export default OrderList;
